/**
 * Pure role-resolution rules, kept out of `AuthContext` so they can be tested
 * without a Supabase client or a React tree.
 */

export type UserRole =
  | 'admin'
  | 'contractor'
  | 'buyer'
  | 'inspector'
  | 'super_admin'
  | 'contractor_staff'

/** Role assumed before any profile has loaded. Has no page permissions. */
export const UNRESOLVED_ROLE: UserRole = 'contractor'

/**
 * Supabase `user_roles` order is not stable, so `roles[0]` is wrong. Pick one
 * display/login role: org roles (admin, contractor, staff) and inspector
 * before evaluator (`buyer`).
 */
const ROLE_PRECEDENCE: readonly UserRole[] = [
  'super_admin',
  'admin',
  'contractor_staff',
  'contractor',
  'inspector',
  'buyer'
]

/**
 * Resolves the primary role from a loaded role list.
 *
 * Returns `null` for an empty list rather than guessing. An empty list means
 * "we do not know", and guessing a role there is how a staff member on a weak
 * connection used to end up with the buyer console.
 */
export function pickPrimaryRole (roles: UserRole[] | null | undefined): UserRole | null {
  if (!roles?.length) return null
  for (const candidate of ROLE_PRECEDENCE) {
    if (roles.includes(candidate)) return candidate
  }
  return roles[0] ?? null
}

/**
 * Role to apply when the profile request failed.
 *
 * Fails closed: keeps whatever was already established, and otherwise stays on
 * the permissionless default. It must never invent a role, because the API
 * would reject every action taken under it and the UI would be a lie.
 */
export function roleAfterProfileFailure (previousRole: UserRole | null): UserRole {
  return previousRole ?? UNRESOLVED_ROLE
}

/** Roles that belong in the dedicated staff PWA rather than the admin console. */
export function isStaffRole (role: UserRole): boolean {
  return role === 'contractor_staff'
}

/**
 * Roles that have their own shell (`/staff/*`, `/buyer/*`, `/inspector/*`)
 * and must not use the evaluation console at `/`.
 */
export function isPortalRole (role: UserRole): boolean {
  return role === 'contractor_staff' || role === 'buyer' || role === 'inspector'
}

/** Roles allowed to render the evaluation console (admin catch-all). */
export function isAdminConsoleRole (role: UserRole): boolean {
  return !isPortalRole(role)
}

/** Landing route for a resolved role. */
export function homeRouteForRole (role: UserRole): string {
  switch (role) {
    case 'contractor_staff': return '/staff/home'
    case 'buyer':            return '/buyer/home'
    case 'inspector':        return '/inspector/home'
    default:                 return '/'
  }
}

/** After CHI/admin locks a work log, console users return to Work Log. */
export function pathAfterEvaluationFinalize (role: UserRole, returnTo?: string | null): string {
  const safeReturn = typeof returnTo === 'string'
    && returnTo.startsWith('/')
    && !returnTo.startsWith('//')
  if (safeReturn) return returnTo as string
  if (isAdminConsoleRole(role)) return '/work-log'
  return '/buyer/tasks'
}
